import { API_BASE_URL } from "../../constant/constant";
import React, { forwardRef, useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Container,
  HeaderDiv,
  ListHeader,
  SidebarHeader,
  TableText,
  SidebarBtn,
  RowDiv,
  RowText,
  DeleteDiv,
  DeleteTxt,
} from "./Style";
import CloseX from "../../assets/x-close.svg";
import EmptySquare from "../../assets/square.svg";
import DeleteIcon from "../../assets/delete.svg";
import EyeIcon from "../../assets/eye.svg";
import UploadIcon from "../../assets/upload-cloud.svg";
import CheckIcon from "../../assets/blue-check.svg";
import UploadModal from "../modals/UploadModal";
import RemoveFileModal from "../modals/RemoveFileModal";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";


const GenSearchSideBar = forwardRef(({ onClose, onUpdate }, ref) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [showRemoveFileModal, setShowRemoveFileModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [currFileUrls, setCurrFileUrls] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);
  const [remainingFileUrls, SetRemainingFileUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalFileSizeMB, setTotalFileSizeMB] = useState(0);
  const containerRef = useRef(null);
  const { t } = useTranslation();

  // Helper function to format file size dynamically
  const formatFileSize = (bytes) => {
    if (bytes < 1024) {
      return `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    } else if (bytes < 1024 * 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    } else {
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    }
  };

  // Helper function to truncate filename
  const truncateFileName = (fileName, maxLength = 20) => {
    if (fileName.length <= maxLength) return fileName;

    const extension = fileName.substring(fileName.lastIndexOf('.'));
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 3);

    return `${truncatedName}...${extension}`;
  };

  const fetchFiles = async () => {
    try {
      const id = localStorage.getItem("id");
      const response = await axios.get(`${API_BASE_URL}/api/auth/get-files`, {
        params: { user_id: id },
      });

      if (response.data && response.data.responseCode === 200) {
        setUploadedFiles(response.data.files);

        const totalSizeBytes = response.data.files.reduce((acc, file) => acc + file.fileSize, 0);
        const totalSizeMB = totalSizeBytes / 1024 / 1024;
        setTotalFileSizeMB(totalSizeMB.toFixed(2));
      }
    } catch (error) {
      console.error("Error fetching files", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  // Handle click outside to close sidebar
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside modals
      if (isModalOpen || showRemoveFileModal) return;

      // Check if click is outside the container
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isModalOpen, showRemoveFileModal, onClose]);

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleModalOpen = () => {
    setCurrFileUrls(uploadedFiles.map(file => file.fileUrl));
    setModalOpen(true);
  };

  const handleShowRemoveFileModal = () => {
    setCurrFileUrls(uploadedFiles.map(file => file.fileUrl));
    setShowRemoveFileModal(true);
  };

  const handleShowRemoveSingleFileModal = (file) => {
    setCurrFileUrls(uploadedFiles.map(file => file.fileUrl));
    setFilesToDelete([file]);
    setShowRemoveFileModal(true);
  };

  const handleHideRemoveFileModal = () => {
    setFilesToDelete([]);
    setShowRemoveFileModal(false);
  };

  const handleFileSelect = (file) => {
    if (filesToDelete.includes(file)) {
      setFilesToDelete(filesToDelete.filter(f => f !== file));
    } else {
      setFilesToDelete([...filesToDelete, file]);
    }
  };

  const handleSelectAll = () => {
    if (filesToDelete.length === uploadedFiles.length) {
      setFilesToDelete([]);
    } else {
      setFilesToDelete(uploadedFiles);
    }
  };

  const handleDeleteClick = () => {
    handleShowRemoveFileModal();
  };

  const isSelected = (file) => filesToDelete.includes(file);

  return (
    <>
      <Container ref={containerRef}>
        <HeaderDiv style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SidebarHeader>{t("sidebar.files")}</SidebarHeader>

          <Tooltip
            title={t("sidebar.filesTooltip")}
            placement="top"
          >
            <InfoCircleOutlined
              style={{
                fontSize: 14,
                color: "#8c8c8c",
                cursor: "pointer",
              }}
            />
          </Tooltip>

          <SidebarBtn onClick={handleModalOpen}>
            <img src={UploadIcon} alt="upload" />
            {t("sidebar.upload")}
          </SidebarBtn>

          <img
            src={CloseX}
            alt="close"
            style={{ marginLeft: "auto", cursor: "pointer" }}
            onClick={onClose}
          />
        </HeaderDiv>

        {filesToDelete.length > 0 && (
          <DeleteDiv>
            <DeleteTxt>{filesToDelete.length} rows selected</DeleteTxt>
            <SidebarBtn
              style={{ background: "#E5322D", marginLeft: "auto" }}
              onClick={handleDeleteClick}
            >
              {t("sidebar.delete")}
            </SidebarBtn>
          </DeleteDiv>
        )}
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress />
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", padding: "1.5rem" }}>
            <ListHeader>
              <img
                src={filesToDelete.length === uploadedFiles.length ? CheckIcon : EmptySquare}
                alt="select"
                style={{ marginRight: "0.75rem", cursor: "pointer" }}
                onClick={handleSelectAll}
              />
              <TableText style={{ flex: "1" }}>Name</TableText>
              <TableText style={{ flex: "1" }}>File Size</TableText>
              <TableText style={{ flex: "1" }}>Upload Time</TableText>
              <TableText style={{ flex: "1", textAlign: "center" }}>Actions</TableText>
            </ListHeader>
            {uploadedFiles.map((file, index) => (
              <RowDiv key={index}>
                <img
                  src={isSelected(file) ? CheckIcon : EmptySquare}
                  alt="select"
                  style={{ marginRight: "0.75rem", cursor: "pointer" }}
                  onClick={() => handleFileSelect(file)}
                />
                <Tooltip title={file.fileName} arrow placement="top">
                  <RowText style={{ flex: "1", cursor: "default" }}>
                    {truncateFileName(file.fileName)}
                  </RowText>
                </Tooltip>
                <RowText style={{ flex: "1" }}>
                  {formatFileSize(file.fileSize)}
                </RowText>
                <RowText style={{ flex: "1" }}>
                  {new Date(file.uploadTime).toLocaleString()}
                </RowText>
                <div style={{ display: "flex", flex: "1", justifyContent: "center", gap: "10px" }}>
                  <img
                    src={EyeIcon}
                    style={{ cursor: "pointer", width: "18px" }}
                    onClick={() => window.open(file.fileUrl, "_blank")}
                  />
                  <img
                    src={DeleteIcon}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleShowRemoveSingleFileModal(file)}
                  />
                </div>
              </RowDiv>
            ))}
          </div>
        )}
      </Container>
      {isModalOpen && (
        <UploadModal
          onClose={handleModalClose}
          currFileUrls={currFileUrls}
          onUpdate={() => {
            fetchFiles();
            onUpdate();
          }}
          totalFileSizeMB={totalFileSizeMB}
        />
      )}
      {showRemoveFileModal && (
        <RemoveFileModal
          file={filesToDelete}
          onClose={handleHideRemoveFileModal}
          onUpdate={() => {
            fetchFiles();
            onUpdate();
          }}
          currFileUrls={currFileUrls}
        />
      )}
    </>
  );
});

export default GenSearchSideBar;